import type { ReactElement } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

interface FormulaHeaderTooltipProps {
  /** Full column name (with units / symbols). */
  label: string;
  /** Calculation expression — rendered in monospace under a "Formula" label. */
  formula?: string;
  /** Optional extra context: intermediate definitions or the raw-data source. */
  note?: string;
  /**
   * The header cell that triggers the tooltip — a single element that can hold
   * a ref (e.g. `<th>` or MUI `<TableCell>`). MUI Tooltip clones it.
   */
  children: ReactElement;
}

/**
 * Wraps a grid header cell in a tooltip that shows how the column is derived.
 * Built for FEAT-2026-0009 — lab operators hover a computed-column header to
 * read the exact expression and confirm the calculation is right.
 *
 * Unlike a plain string `title`, the formula renders on its own line in
 * monospace (a string `\n` collapses to a space inside MUI Tooltip).
 */
export const FormulaHeaderTooltip = ({ label, formula, note, children }: FormulaHeaderTooltipProps) => {
  const title = (
    <Box sx={{ py: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.4 }}>
        {label}
      </Typography>

      {formula ? (
        <Box sx={{ mt: 0.75 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}
          >
            Formula
          </Typography>
          <Box
            component="code"
            sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.78rem', mt: 0.25, whiteSpace: 'pre-wrap' }}
          >
            {formula}
          </Box>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8, fontStyle: 'italic', lineHeight: 1.35 }}>
          {note ?? 'Measured value (not calculated).'}
        </Typography>
      )}

      {formula && note && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.8, lineHeight: 1.35 }}>
          {note}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={title} arrow placement="top" componentsProps={{ tooltip: { sx: { maxWidth: 340 } } }}>
      {children}
    </Tooltip>
  );
};
